import { Link, Form } from "@npco/zeller-design-system";
import { bindField, useForm } from "@npco/zeller-design-system/form";
import * as Input from "@npco/zeller-design-system/input";
import type { ADT } from "pb.adt";
import { useCallback, useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import { z } from "zod";

import { logger } from "../../../shared/logger";
import { Layout } from "../shared/layout";
import type { Page } from "../terminal-setup";
import { AvailableTerminals } from "./page-paired/available-terminals";
import { ConnectionCard } from "./page-paired/connection-card";
import { TerminalPreview } from "./page-paired/terminal-preview";
import type { ConnectionCheck } from "./use-connection-status";

const DeviceNameValuesSchema = z.object({
	deviceName: z.string(),
});

export function PagePaired({
	page: {
		deviceId,
		deviceName: deviceNameInitial,
		terminalId,
		terminalName,
		terminalModel,
		terminalColor,
		terminalLogoUrl,
		onRename,
		onResetDevice,
		onChangePairing,
		onPair,
	},
	connection,
	onClose,
	onLogout,
	pending: _pending,
}: {
	page: ADT.Pick<Page, "Paired">;
	connection: ConnectionCheck;
} & Required<Pick<Layout.Props, "onClose" | "onLogout" | "pending">>) {
	const form = useForm(
		DeviceNameValuesSchema,
		async ({ value: { deviceName }, formApi }) => {
			const response = await onRename(deviceName);
			if (response instanceof Error) {
				logger.error("setup: saving rename device error", {
					errorName: response.name,
					errorMessage: response.message,
					errorStack: response.stack,
					errorCause: response.cause,
				});
				return formApi.setErrorMap({
					onSubmit: "Failed." as unknown as undefined,
				});
			}
			formApi.reset();
			setRenaming(false);
		},
	);

	// Merge "reset" "pairing" "rename" and form submitting pending states into one.
	const [localPending, setLocalPending] = useState<"reset" | "pairing">();
	const [renaming, setRenaming] = useState(false);
	const onClickRename = useCallback(() => {
		form.setFieldValue("deviceName", deviceNameInitial);
		setRenaming(true);
	}, [form, deviceNameInitial]);
	const onClickRenameCancel = useCallback(() => {
		form.reset();
		setRenaming(false);
	}, [form]);

	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => {
				const pending = _pending || (isSubmitting ? "renaming" : localPending);
				return (
					<Layout
						title="Device Details"
						onClose={onClose}
						onLogout={onLogout}
						pending={pending === "logout" ? pending : !!pending}
						actions={[
							{
								label: "Reset Device",
								position: "start",
								destructive: true,
								onClick: () => {
									setLocalPending("reset");
									return onResetDevice();
								},
								confirm: {
									title: "Reset Device",
									content: "Are you sure you want to reset this device?",
									label: "Reset",
								},
							},
						]}
					>
						<div className="flex px-56 pt-6 transition-all">
							<TerminalPreview
								className={twMerge(
									"hidden [@media(min-height:550px)]:md:block",
									"transition-all",
									"pt-20 [@media(min-height:625px)]:pt-64",
								)}
								model={terminalModel}
								terminalColour={terminalColor}
								terminalLogoUrl={terminalLogoUrl}
							/>
							<div className="flex flex-1 flex-col gap-40">
								<section className="flex flex-col gap-24">
									<Form onSubmit={form.handleSubmit}>
										<form.Field
											name="deviceName"
											children={(field) =>
												!renaming ? (
													<div
														className={twJoin(
															"h-[56px]",
															"flex flex-row items-center justify-between",
															"border-b",
															"border-grey-90",
															"dark:border-grey-800",
														)}
													>
														<div className="text-lg/heading">
															{deviceNameInitial}
														</div>
														<div>
															<Link
																label="Rename"
																onClick={onClickRename}
																disabled={!!pending}
															/>
														</div>
													</div>
												) : (
													<div className="h-[56px]">
														<Input.Text
															{...bindField(field)}
															label="POS Register Name"
															error={!!field.form.state.errors.length}
															disabled={isSubmitting}
															actions={[
																{
																	label: "Save",
																	onClick: () => undefined,
																	disabled: !!pending,
																	pending: pending === "renaming",
																	$button: { type: "submit" },
																},
																{
																	label: "Cancel",
																	onClick: onClickRenameCancel,
																	disabled: !!pending,
																},
															]}
														/>
													</div>
												)
											}
										/>
									</Form>
								</section>
								<ConnectionCard
									terminalName={terminalName}
									connection={connection}
									disabled={!!pending}
									onUnpair={onChangePairing}
								/>
								<AvailableTerminals
									deviceId={deviceId}
									terminalId={terminalId}
									terminalName={terminalName}
									disabled={!!pending}
									onPair={async (id) => {
										setLocalPending("pairing");
										await onPair(id);
										setLocalPending(undefined);
									}}
								/>
							</div>
						</div>
					</Layout>
				);
			}}
		</form.Subscribe>
	);
}
